import React, { useCallback, useEffect, useState } from 'react';
import { teamColors } from './teamColors';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faMedal,
  faArrowUpRightFromSquare,
  faPaperPlane,
  faPen,
  faTrashCan,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faGift,
  faStar,
  faLink,
  faUsers,
  faCrown,
  faUserCheck,
  faCheck,
  faScrewdriverWrench,
  faLock,
  faCalendarDays,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Countdown from './Countdown';
import Participants from './Participants';
import Badges from './Badges';
import './CompetitionsBoard.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const LEVEL = { all: 'All students', little: 'Little (6–8)', junior: 'Junior (9+)' };
const PLACE = { 1: '1st place', 2: '2nd place', 3: '3rd place' };
const day = (s, opts = { month: 'short', day: 'numeric' }) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', opts) : '';
};
// " at 9:00 PM" when a challenge ends at a set time (empty when it ends at midnight, as before)
const endAt = (c) => {
  const m = String(c?.endTime || '').match(/^(\d{2}):(\d{2})$/);
  if (!m || c.endTime === '23:59') return '';
  return ` at ${new Date(2000, 0, 1, +m[1], +m[2]).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};
const EMPTY = { title: '', link: '', note: '' };

/* Send / edit my project (students, while the challenge is open) */
const EntryForm = ({ comp, onSaved }) => {
  const [editing, setEditing] = useState(!comp.myEntry);
  const [form, setForm] = useState(comp.myEntry ? { title: comp.myEntry.title, link: comp.myEntry.link, note: comp.myEntry.note } : EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async (e) => {
    e.preventDefault();
    if (!/^https?:\/\/\S{3,}$/i.test(form.link.trim())) return setError('Paste the link to your project (it starts with https://).');
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/api/student/competitions/${comp.id}/entry`, form, auth());
      setEditing(false);
      onSaved('Your project was sent. Good luck! 🍀');
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t send your project. Please try again.');
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const remove = async () => {
    if (!window.confirm('Remove your project from this challenge?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/student/competitions/${comp.id}/entry`, auth());
      setForm(EMPTY);
      setEditing(true);
      onSaved('Your project was removed.');
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t remove it.');
    } finally {
      setBusy(false);
    }
  };

  if (comp.myEntry && !editing) {
    return (
      <div className="rmcmp-mine">
        <FontAwesomeIcon icon={faCircleCheck} />
        <div>
          <b>Your project is in!</b>
          <a href={comp.myEntry.link} target="_blank" rel="noopener noreferrer">
            {comp.myEntry.title || 'Open my project'} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
          <small>You can change it until {day(comp.endDate, { month: 'long', day: 'numeric' })}{endAt(comp)}.</small>
          <a
            className="rmtl-share"
            href={`https://api.whatsapp.com/send/?text=${encodeURIComponent(
              `I built a project for the "${comp.title}" challenge at RoboMind! 🚀 Have a look: ${comp.myEntry.link}`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FontAwesomeIcon icon={faWhatsapp} /> Share with my family
          </a>
        </div>
        <div className="rmcmp-mine-actions">
          <button type="button" onClick={() => setEditing(true)} disabled={busy} aria-label="Edit my project">
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button type="button" className="is-danger" onClick={remove} disabled={busy} aria-label="Remove my project">
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="rmcmp-form" onSubmit={save}>
      <label>
        <span>Project link *</span>
        <input type="url" inputMode="url" placeholder="https://scratch.mit.edu/projects/…" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} disabled={busy} required />
      </label>
      <label>
        <span>Project name</span>
        <input type="text" maxLength={120} placeholder="My space game" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} disabled={busy} />
      </label>
      <div className="rmcmp-linknote rmcmp-form-wide">
        <FontAwesomeIcon icon={faLink} />
        <div>
          <b>Make sure everyone can open your link!</b>
          <span>
            Send the <b>project link</b> itself. Scratch: click <b>Share</b> on your project. Code.org, Thunkable, App Inventor, Replit, Tinkercad: use the <b>Share</b> or <b>public link</b>. AI Creators: use the tool’s <b>Share</b> link. Open the link in a private window to check it works.
          </span>
        </div>
      </div>
      <label className="rmcmp-form-wide">
        <span>What did you make?</span>
        <textarea rows={2} maxLength={500} placeholder="Tell the teachers about your project" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} disabled={busy} />
      </label>
      {error && <p className="rmcmp-error rmcmp-form-wide">{error}</p>}
      <div className="rmcmp-form-wide rmcmp-form-actions">
        {comp.myEntry && (
          <button type="button" className="rmcmp-btn rmcmp-btn--ghost" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </button>
        )}
        <button type="submit" className="rmcmp-btn" disabled={busy}>
          <FontAwesomeIcon icon={faPaperPlane} /> {busy ? 'Sending…' : comp.myEntry ? 'Save changes' : 'Send my project'}
        </button>
      </div>
    </form>
  );
};

/* Long challenge text: the first lines, then "Read more" */
const ReadMore = ({ text }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > 260 || text.split('\n').length > 4;
  return (
    <div className={`rmcmp-descwrap ${long && !open ? 'is-closed' : ''}`}>
      <span className="rmcmp-desc-label">
        <FontAwesomeIcon icon={faStar} /> The challenge
      </span>
      <p className={`rmcmp-desc ${long && !open ? 'is-short' : ''}`}>{text}</p>
      {long && (
        <button type="button" className="rmcmp-more" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Show less ▲' : 'Read the full challenge ▼'}
        </button>
      )}
    </div>
  );
};

/* Score buttons 1-10 (teachers, while voting is open) */
const Score = ({ entry, onScore }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (n) => {
    setBusy(true);
    await onScore(entry, n);
    setBusy(false);
  };
  return (
    <div className="rmcmp-score" role="radiogroup" aria-label={`Score for ${entry.title || entry.by}`}>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button key={n} type="button" role="radio" aria-checked={entry.myScore === n} className={entry.myScore === n ? 'is-on' : ''} onClick={() => pick(n)} disabled={busy}>
          {n}
        </button>
      ))}
    </div>
  );
};

const EntryCard = ({ e, mode, voting, onScore, color }) => (
  <li className={`rmcmp-entry ${e.rank ? `is-rank-${e.rank}` : ''} ${e.mine ? 'is-mine' : ''}`} style={color ? { '--rmtl-c': color } : undefined}>
    {e.rank && (
      <span className="rmcmp-medal">
        <FontAwesomeIcon icon={e.rank === 1 ? faTrophy : faMedal} /> {PLACE[e.rank]}
      </span>
    )}
    <div className="rmcmp-entry-who">
      {e.rank && e.photo && <img src={e.photo} alt="" loading="lazy" onError={(ev) => (ev.currentTarget.style.display = 'none')} />}
      <div>
        <h4>{e.title || 'Project'}</h4>
        <p className="rmcmp-by">
          by {e.by}
          {e.mine ? ' (you)' : ''}
          {e.teacher && <span className="rmcmp-team"> · with {e.teacher}</span>}
        </p>
        <Badges b={e.badges} />
      </div>
    </div>
    {e.note && <p className="rmcmp-note">{e.note}</p>}
    <a className="rmcmp-open" href={e.link} target="_blank" rel="noopener noreferrer">
      Open project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
    </a>
    {mode === 'teacher' && voting && e.own && (
      <p className="rmcmp-own">
        <FontAwesomeIcon icon={faUserCheck} /> Your student: the other teachers score this project.
      </p>
    )}
    {mode === 'teacher' && voting && !e.own && <Score entry={e} onScore={onScore} />}
    {mode === 'teacher' && !voting && e.myScore && (
      <p className="rmcmp-myscore">
        <FontAwesomeIcon icon={faStar} /> Your score: {e.myScore}/10
      </p>
    )}
  </li>
);

/* "12 students are taking part · 5 projects sent" */
const PeopleHead = ({ c, mode }) => {
  const list = c.participants || [];
  const sent = list.filter((p) => p.sent).length;
  return (
    <h4 className="rmcmp-sub">
      <FontAwesomeIcon icon={faUsers} />{' '}
      {list.length
        ? `${list.length} ${list.length === 1 ? 'student is' : 'students are'} taking part${c.status === 'open' ? ` · ${sent} ${sent === 1 ? 'project' : 'projects'} sent` : ''}`
        : mode === 'teacher'
        ? 'No student joined yet'
        : 'Be the first to join!'}
    </h4>
  );
};

/* Teams: students per teacher ("Team Rasha · 5 sent · 7 joined"), best team crowned at the end */
const Teams = ({ c }) => {
  const list = c.teams || [];
  if (!list.length) return null;
  const done = c.status === 'finished';
  const colors = teamColors([...list.map((t) => t.name), ...(c.participants || []).map((p) => p.teacher)]);
  return (
    <ul className="rmcmp-teams" aria-label="Teams">
      {list.map((t) => (
        <li key={t.id} className={`${t.best ? 'is-best' : ''} ${t.mine ? 'is-mine' : ''}`} style={{ '--rmcmp-team-c': colors.get(t.name) }}>
          {t.best && <FontAwesomeIcon icon={faCrown} />}
          <b>Team {t.name}</b>
          <span>
            {c.status === 'upcoming'
              ? `${t.joined} joined`
              : `${t.sent} ${t.sent === 1 ? 'project' : 'projects'}${done ? '' : ` · ${t.joined} joined`}`}
          </span>
          {t.best && <small>Best team</small>}
        </li>
      ))}
    </ul>
  );
};

/* Before the start: "I accept the challenge" (my name shows with the others) */
const JoinBox = ({ comp, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isOpen = comp.status === 'open';
  const start = day(comp.startDate, { weekday: 'long', month: 'long', day: 'numeric' });
  const end = `${day(comp.endDate, { weekday: 'long', month: 'long', day: 'numeric' })}${endAt(comp)}`;

  const go = async (join) => {
    setBusy(true);
    setError('');
    try {
      if (join) await axios.post(`${API}/api/student/competitions/${comp.id}/join`, {}, auth());
      else await axios.delete(`${API}/api/student/competitions/${comp.id}/join`, auth());
      onSaved(join ? (isOpen ? 'You’re in! 🚀 Send your project below when it’s ready.' : 'You’re in! 🚀 Start thinking about your project.') : 'You left the challenge.');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (comp.joined) {
    return (
      <div className="rmcmp-mine">
        <FontAwesomeIcon icon={faCircleCheck} />
        <div>
          <b>You accepted the challenge! 🚀</b>
          <small>
            {isOpen ? `Send your project before ${end}.` : `You can send your project from ${start}. Start thinking about your idea!`} Changed your mind? You can leave anytime.
          </small>
        </div>
        <button type="button" className="rmcmp-leave" onClick={() => window.confirm('Leave this challenge?') && go(false)} disabled={busy}>
          Leave
        </button>
      </div>
    );
  }
  return (
    <div className="rmcmp-join">
      <p>
        {isOpen ? (
          <>
            <b>You can still join!</b> The challenge has started, and you have until <b>{end}</b> to send your project.
          </>
        ) : (
          <>
            Accept the challenge now (or any time until <b>{end}</b>): your name shows with the other participants, and you can send your project from <b>{start}</b>.
          </>
        )}
      </p>
      <button type="button" className="rmcmp-btn rmcmp-btn--big" onClick={() => go(true)} disabled={busy}>
        <FontAwesomeIcon icon={faTrophy} /> {busy ? 'Joining…' : 'I accept the challenge!'}
      </button>
      <small className="rmcmp-join-note">You can leave anytime.</small>
      {error && <p className="rmcmp-error">{error}</p>}
    </div>
  );
};

/* The rules in 4 short lines (students often thought they had to join before the start) */
const HowItWorks = ({ c }) => {
  const end = day(c.endDate, { month: 'long', day: 'numeric' });
  return (
    <ul className="rmcmp-how" aria-label="How it works">
      <li>
        <span aria-hidden="true">🗓️</span>
        <p>
          <b>Join anytime</b> before the start or while it’s open, until {end}.
        </p>
      </li>
      <li>
        <span aria-hidden="true">👋</span>
        <p>
          <b>Changed your mind?</b> You can leave anytime.
        </p>
      </li>
      <li>
        <span aria-hidden="true">🧑‍🏫</span>
        <p>
          <b>Need help?</b> Your teacher will guide you.
        </p>
      </li>
      <li>
        <span aria-hidden="true">⭐</span>
        <p>
          <b>Teachers score every project</b>, and the top scores win 🏆
        </p>
      </li>
    </ul>
  );
};

/* ===== Timeline look: steps on the left, "what to do now" + teams + winners + projects on the right ===== */

/* where the challenge (and this student) is: 0 join · 1 build & send · 2 teachers vote · 3 winners */
const stepsOf = (c, mode) => {
  const joined = !!(c.joined || c.myEntry);
  const sent = !!c.myEntry;
  const live = c.status === 'upcoming' || c.status === 'open';
  const at = { upcoming: 0, open: 1, judging: 2, finished: 3 }[c.status];
  const state = (i) => {
    if (mode !== 'student') return i < at ? 'done' : i === at ? 'now' : 'todo';
    if (i === 0) return joined ? 'done' : live ? 'now' : 'off';
    if (i === 1) return sent ? 'done' : joined && c.status === 'open' ? 'now' : c.status === 'judging' || c.status === 'finished' ? 'off' : 'todo';
    if (i === 2) return c.status === 'judging' ? 'now' : c.status === 'finished' ? 'done' : 'todo';
    return c.status === 'finished' ? 'now' : 'todo';
  };
  return [
    { label: mode === 'student' ? (joined ? 'Joined' : 'Join') : 'Students join', sub: joined && mode === 'student' ? 'You’re in!' : `Anytime until ${day(c.endDate)}`, icon: faUserCheck },
    { label: 'Build & send', sub: sent && mode === 'student' ? 'Project sent' : `${day(c.startDate)} – ${day(c.endDate)}`, icon: faScrewdriverWrench },
    { label: 'Teachers vote', sub: `After ${day(c.endDate)}`, icon: faStar },
    { label: 'Winners', sub: c.status === 'finished' ? 'Announced!' : 'After the vote', icon: faTrophy },
  ].map((st, i) => ({ ...st, state: state(i) }));
};

const Timeline = ({ steps }) => (
  <ol className="rmtl-line" aria-label="Challenge steps">
    {steps.map((st, i) => (
      <li key={st.label} className={`is-${st.state}`}>
        <span className="rmtl-dot">{st.state === 'done' ? <FontAwesomeIcon icon={faCheck} /> : i + 1}</span>
        <div>
          <b>{st.label}</b>
          <small>{st.sub}</small>
        </div>
      </li>
    ))}
  </ol>
);

/* team race: one row per team in its color, a bar for projects sent (before the start: students joined), leader in gold */
const TeamTiles = ({ c, colors, myTeam, mode }) => {
  const list = c.teams || [];
  if (!list.length) return null;
  const metric = (t) => (c.status === 'upcoming' ? t.joined : t.sent);
  const sorted = [...list].sort((a, b) => metric(b) - metric(a) || b.joined - a.joined || a.name.localeCompare(b.name));
  const max = Math.max(1, ...sorted.map(metric));
  return (
    <ol className="rmtl-race">
      {sorted.map((t, i) => {
        const mine = t.mine || (myTeam && myTeam === t.name);
        return (
          <li key={t.id} className={`${t.best ? 'is-best' : ''} ${mine ? 'is-mine' : ''}`} style={{ '--rmtl-c': colors.get(t.name) }}>
            <span className="rmtl-rank">{i + 1}</span>
            <span className="rmtl-team">
              <span className="rmtl-av">{t.name.charAt(0).toUpperCase()}</span>
              <span>
                Team {t.name}
                {t.best && (
                  <small className="rmtl-tag is-best">
                    <FontAwesomeIcon icon={faCrown} /> Best team
                  </small>
                )}
                {mine && <small className="rmtl-tag is-mine">{mode === 'teacher' ? 'Your students' : 'Your team'}</small>}
              </span>
            </span>
            <span className="rmtl-bar" aria-hidden="true">
              <span style={{ width: `${Math.round((metric(t) / max) * 100)}%` }} />
            </span>
            <span className="rmtl-score">
              {c.status === 'upcoming' ? (
                <>
                  {t.joined} joined
                </>
              ) : (
                <>
                  {t.sent} {t.sent === 1 ? 'project' : 'projects'}
                  <small>{t.joined} joined</small>
                </>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

/* winners (or 3 locked places until they're announced) */
const WinnersBox = ({ c, winners }) => (
  <ul className="rmtl-wins">
    {[1, 2, 3].map((r) => {
      const e = winners.find((x) => x.rank === r);
      return (
        <li key={r} className={`is-${r} ${e ? 'is-on' : ''}`}>
          <span className="rmtl-medal">
            {e?.photo ? <img src={e.photo} alt="" loading="lazy" onError={(ev) => (ev.currentTarget.style.display = 'none')} /> : <FontAwesomeIcon icon={r === 1 ? faTrophy : faMedal} />}
          </span>
          <div>
            <small>{PLACE[r]}</small>
            {e ? (
              <>
                <b>
                  {e.by}
                  {e.mine ? ' (you)' : ''}
                </b>
                <span>
                  {e.title || 'Project'}
                  {e.teacher ? ` · Team ${e.teacher}` : ''}
                </span>
                <a href={e.link} target="_blank" rel="noopener noreferrer">
                  Open project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              </>
            ) : (
              <span>{r === 1 ? 'Champion badge' : r === 2 ? 'Silver medal badge' : 'Bronze medal badge'} · project on the Wall of Fame</span>
            )}
          </div>
        </li>
      );
    })}
  </ul>
);

const STATUS = { upcoming: 'Coming soon', open: 'Open now', judging: 'Teachers are voting', finished: 'Finished' };

/* winners on a podium: 2nd · 1st · 3rd */
const Podium = ({ winners }) => {
  const order = [2, 1, 3].map((r) => winners.find((e) => e.rank === r)).filter(Boolean);
  return (
    <div className="rmtl-podium">
      {order.map((e) => (
        <div key={e.id} className={`rmtl-pd is-${e.rank}`}>
          <span className="rmtl-pd-medal">
            {e.photo ? (
              <img src={e.photo} alt="" loading="lazy" onError={(ev) => (ev.currentTarget.style.display = 'none')} />
            ) : (
              <FontAwesomeIcon icon={e.rank === 1 ? faTrophy : faMedal} />
            )}
          </span>
          <b className="rmtl-pd-name">
            {e.by}
            {e.mine ? ' (you)' : ''}
          </b>
          <span className="rmtl-pd-title">{e.title || 'Project'}</span>
          {e.teacher && <span className="rmtl-pd-team">Team {e.teacher}</span>}
          <a href={e.link} target="_blank" rel="noopener noreferrer">
            Open project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
          <span className="rmtl-pd-base">{e.rank === 1 ? '1st' : e.rank === 2 ? '2nd' : '3rd'}</span>
        </div>
      ))}
    </div>
  );
};

const CompetitionCard = ({ c, mode, canEnter, onSaved, onScore, onTimeUp, myTeam, onToast }) => {
  const voting = c.status === 'judging';
  const winners = c.entries.filter((e) => e.rank).sort((a, b) => a.rank - b.rank);
  const others = c.entries.filter((e) => !e.rank);
  const colors = teamColors([...(c.teams || []).map((t) => t.name), ...(c.participants || []).map((p) => p.teacher), ...c.entries.map((e) => e.teacher)]);
  const steps = stepsOf(c, mode);
  const student = mode === 'student' && canEnter;
  const joined = !!(c.joined || c.myEntry);

  // the "Now" card: what this student (or teacher) has to do right now
  let nowTitle = '';
  let nowText = '';
  if (mode === 'student') {
    if (c.status === 'upcoming') {
      nowTitle = joined ? 'You’re in! Get ready' : 'Join the challenge';
      nowText = joined ? `Building starts on ${day(c.startDate, { weekday: 'long', month: 'long', day: 'numeric' })}. Think about your idea with your teacher.` : 'Accept the challenge: your name shows with the other students.';
    } else if (c.status === 'open') {
      nowTitle = c.myEntry ? 'Your project is sent 🎉' : joined ? 'Now: build your project and send it' : 'Join and send your project';
      nowText = c.myEntry
        ? `You can still change it until ${day(c.endDate, { month: 'long', day: 'numeric' })}${endAt(c)}.`
        : `Paste the share link of your project. You can change it until ${day(c.endDate, { month: 'long', day: 'numeric' })}${endAt(c) || ' (last day)'}.`;
    } else if (voting) {
      nowTitle = 'Teachers are voting';
      nowText = 'The teachers are looking at every project. Winners are announced soon!';
    } else {
      nowTitle = winners.length ? 'The winners are here! 🏆' : 'This challenge is finished';
      nowText = winners.length ? 'Look at the winners and every project below.' : '';
    }
  } else if (c.status === 'upcoming' || c.status === 'open') {
    nowTitle = c.status === 'open' ? 'Students are building' : 'Coming soon';
    nowText = 'Encourage your students to join. Projects show here when the challenge closes, then you score each one from 1 to 10.';
  } else if (voting) {
    nowTitle = c.toScore > 0 ? `${c.toScore} ${c.toScore === 1 ? 'project' : 'projects'} to score` : 'You scored every project. Thank you!';
    nowText = '1 = needs work, 10 = amazing. You don’t score your own students.';
  } else {
    nowTitle = winners.length ? 'Winners announced' : 'Challenge finished';
  }

  return (
    <article className={`rmcmp-card rmtl rmch is-${c.status}`}>
      {/* ---------- top: colored band (status, month, countdown) + the challenge ---------- */}
      <header className="rmch-top rmtl-head">
        <aside className="rmch-band">
          <span className="rmch-status">
            <i aria-hidden="true" /> {STATUS[c.status]}
          </span>
          <span className="rmch-month">{day(c.startDate, { month: 'short' }).toUpperCase()}</span>
          {c.status === 'upcoming' && <Countdown to={c.startsAt} label="Starts in" onDone={onTimeUp} />}
          {c.status === 'open' && <Countdown to={c.endsAt} label={`Ends in · ${day(c.endDate)}${endAt(c) || ' (last day)'}`} onDone={onTimeUp} />}
          {voting && (
            <p className="rmch-band-note">
              <FontAwesomeIcon icon={faStar} /> {mode === 'teacher' ? 'Score the projects' : 'Winners are announced soon!'}
            </p>
          )}
          {c.status === 'finished' && (
            <p className="rmch-band-note">
              <FontAwesomeIcon icon={faTrophy} /> {c.entries.length} {c.entries.length === 1 ? 'project' : 'projects'} sent
            </p>
          )}
        </aside>
        <div className="rmch-main">
          <div className="rmch-chips">
            <span>{LEVEL[c.level] || LEVEL.all}</span>
            <span>
              <FontAwesomeIcon icon={faCalendarDays} /> {day(c.startDate)} – {day(c.endDate)}
            </span>
          </div>
          <h3>{c.title}</h3>
          {c.description && <ReadMore text={c.description} />}
          {c.prize && (
            <p className="rmcmp-prize">
              <FontAwesomeIcon icon={faGift} /> {c.prize}
            </p>
          )}
        </div>
      </header>

      {/* ---------- steps + content ---------- */}
      <div className="rmtl-body">
        <Timeline steps={steps} />
        <div className="rmtl-cards">
          <section className="rmtl-card is-now">
            <h4>
              <FontAwesomeIcon icon={voting ? faStar : c.status === 'finished' ? faTrophy : faScrewdriverWrench} /> {nowTitle}
            </h4>
            {nowText && <p className="rmtl-text">{nowText}</p>}
            {student && c.status === 'upcoming' && <JoinBox comp={c} onSaved={onSaved} />}
            {student && c.status === 'open' && !c.myEntry && <JoinBox comp={c} onSaved={onSaved} />}
            {student && c.status === 'open' && joined && <EntryForm comp={c} onSaved={onSaved} />}
            {(c.status === 'upcoming' || c.status === 'open') && <HowItWorks c={c} />}
          </section>

        </div>
      </div>

      {/* ---------- full width rows ---------- */}
      <div className="rmtl-rows">
          {(c.teams || []).length > 0 && (
            <section className="rmtl-card">
              <h4>
                <FontAwesomeIcon icon={faUsers} /> Teams <em>{c.status === 'upcoming' ? 'students joined' : 'projects sent'}</em>
              </h4>
              <TeamTiles c={c} colors={colors} myTeam={myTeam} mode={mode} />
            </section>
          )}

          {(c.status === 'upcoming' || c.status === 'open') && (c.participants || []).length > 0 && (
            <section className="rmtl-card">
              <PeopleHead c={c} mode={mode} />
              <Participants list={c.participants || []} teams={c.teams} />
            </section>
          )}

          <section className={`rmtl-card ${winners.length ? '' : 'is-locked'}`}>
            <h4>
              <FontAwesomeIcon icon={faTrophy} /> Winners
              {!winners.length && (
                <em>
                  <FontAwesomeIcon icon={faLock} /> {c.status === 'finished' ? 'no winners' : 'announced after the vote'}
                </em>
              )}
            </h4>
            {winners.length ? <Podium winners={winners} /> : <WinnersBox c={c} winners={winners} />}
            <p className="rmtl-badge-note">
              <FontAwesomeIcon icon={faMedal} />
              <span>
                The top 3 get a medal badge and their projects go on the Wall of Fame. Every student who sends a project gets the <b>Challenger</b> badge.
              </span>
            </p>
          </section>

          {others.length > 0 && (
            <section className="rmtl-card">
              <h4>
                {winners.length ? 'All projects' : 'Projects'} <em>{others.length} sent</em>
              </h4>
              <ul className="rmcmp-entries rmtl-entries">
                {others.map((e) => (
                  <EntryCard key={e.id} e={e} mode={mode} voting={voting} onScore={onScore} color={e.teacher ? colors.get(e.teacher) : null} />
                ))}
              </ul>
            </section>
          )}
          {(c.status === 'judging' || c.status === 'finished') && c.entries.length === 0 && <p className="rmcmp-hint">No projects were sent for this challenge.</p>}
      </div>
    </article>
  );
};

/* The competitions page for students (mode="student") and teachers (mode="teacher") */
/* mode: 'student' | 'teacher' | 'admin' (the admin sees the student view, without join / send buttons) */
const CompetitionsBoard = ({ mode = 'student', embedded = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const url =
    mode === 'teacher'
      ? `${API}/api/teacher/competitions`
      : mode === 'admin'
      ? `${API}/api/admin/competitions/board`
      : `${API}/api/student/competitions`;
  const cardMode = mode === 'admin' ? 'student' : mode; // the admin preview looks like the student page
  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(url, auth());
      setData(res.data);
    } catch (err) {
      setError(
        mode === 'admin' && err.response?.status === 404
          ? 'The server isn’t updated yet: upload backend/api/Competitions/Competitions.js and restart it to see the student view. Manage works meanwhile.'
          : err.response?.data?.error || 'We couldn’t load the competitions.'
      );
    }
  }, [url, mode]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const onSaved = (msg) => {
    setToast(msg);
    load();
  };

  // teachers: save the score, update it on the page right away
  const onScore = async (entry, score) => {
    try {
      await axios.put(`${API}/api/teacher/competitions/entry/${entry.id}/score`, { score }, auth());
      setData((d) => ({
        ...d,
        competitions: d.competitions.map((c) => {
          if (!c.entries.some((e) => e.id === entry.id)) return c;
          const entries = c.entries.map((e) => (e.id === entry.id ? { ...e, myScore: score } : e));
          return { ...c, entries, toScore: entries.filter((e) => !e.myScore).length };
        }),
      }));
    } catch (err) {
      setToast(err.response?.data?.error || 'Couldn’t save the score.');
    }
  };

  if (error && !data) {
    return (
      <div className="rmcmp">
        <div className="rmcmp-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmcmp-btn" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rmcmp" aria-busy="true">
        <div className="rmcmp-skel rmcmp-skel--head" />
        <div className="rmcmp-skel" />
      </div>
    );
  }

  const comps = data.competitions || [];
  const groups = [
    ['open', 'Open now'],
    ['judging', 'Teachers are voting'],
    ['upcoming', 'Coming soon'],
    ['finished', 'Past challenges'],
  ];

  return (
    <div className="rmcmp">
      {!embedded && (
      <header className="rmcmp-head">
        <span className="rmcmp-eyebrow">
          <FontAwesomeIcon icon={faTrophy} /> Competitions
        </span>
        <h1>{mode === 'teacher' ? 'Student challenges' : 'Monthly challenges'}</h1>
        <p>
          {mode === 'teacher'
            ? 'A new challenge every month. Encourage your students to join, and when a challenge closes, score each project from 1 to 10.'
            : 'A new challenge every month. Build something, send the link, and the teachers pick the winners. Everyone can see the projects once the challenge closes.'}
        </p>
      </header>
      )}



      {comps.length === 0 ? (
        <div className="rmcmp-empty">
          <FontAwesomeIcon icon={faTrophy} />
          <p>The first challenge is coming soon. Stay tuned!</p>
        </div>
      ) : (
        groups.map(([key, label]) => {
          const list = comps.filter((c) => c.status === key);
          if (!list.length) return null;
          return (
            <section key={key} className="rmcmp-group">
              <h2>{label}</h2>
              {list.map((c) => (
                <CompetitionCard key={c.id} c={c} mode={cardMode} canEnter={data.canEnter} onSaved={onSaved} onScore={onScore} onTimeUp={load} myTeam={data.myTeacher} onToast={setToast} />
              ))}
            </section>
          );
        })
      )}

      {toast && (
        <div className="rmcmp-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default CompetitionsBoard;

// shared with the admin Competitions page (same look)
export { ReadMore, Teams, PeopleHead, LEVEL, PLACE, day, endAt };
